import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import { authService } from "../services/api/authService";

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");

    try {
      await authService.forgotPassword(data.email);
      setIsSubmitted(true);
      toast.success(`Password reset link sent to ${data.email}`);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to send reset email. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-success/20 rounded-full mb-4">
              <Icon icon="mdi:check-circle" className="text-4xl text-success" />
            </div>
            <h1 className="text-3xl font-bold text-text-dark mb-2">
              Check Your Email
            </h1>
            <p className="text-text-secondary">
              We've sent a password reset link to{" "}
              <strong>{getValues("email")}</strong>
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-border-light text-center">
            <p className="text-text-secondary mb-6">
              Click the link in the email to reset your password. If you don't
              see it, check your spam folder.
            </p>
            <div className="space-y-3">
              <Button
                variant="secondary"
                className="w-full"
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    await authService.forgotPassword(getValues("email"));
                    toast.success("Reset link resent successfully!");
                  } catch (err) {
                    const errorMessage =
                      err.response?.data?.message ||
                      err.message ||
                      "Failed to resend email. Please try again.";
                    toast.error(errorMessage);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                Resend Email
              </Button>
              <Link to="/login" className="block">
                <Button variant="secondary" className="w-full">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img
            src="logo.png"
            alt="SentryPrime"
            className="mx-auto h-14 w-auto mb-3"
          />
          <p className="text-text-secondary">Reset Your Password</p>
        </div>

        {/* Forgot Password Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border-light">
          <h2 className="text-2xl font-semibold text-text-dark mb-2">
            Forgot Password?
          </h2>
          <p className="text-sm text-text-secondary mb-6">
            Enter your email address and we'll send you a link to reset your
            password.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              {...register("email")}
              error={errors.email?.message}
            />

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full"
              icon="mdi:email-send"
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-2"
            >
              <Icon icon="mdi:arrow-left" />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-text-secondary">
          <p>© 2024 SentryPrime. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
